import { createSlice } from "@reduxjs/toolkit"
import { withFirestoreHydration } from "../middleware/firestorePersistence"

// Initial state for chores
const initialState = {
  chores: {},
  userPoints: {},
  userLevels: {},
  activeUsers: [],
  completedChores: {},
  isLoading: false,
  error: null,
  _hydrated: false,
}

const choresSlice = createSlice({
  name: "chores",
  initialState,
  reducers: {
    // Chore management
    setChores: (state, action) => {
      state.chores = action.payload
      state.isLoading = false
    },

    addChore: (state, action) => {
      const chore = action.payload
      state.chores[chore.id] = chore
    },

    updateChore: (state, action) => {
      const { choreId, updates } = action.payload
      if (state.chores[choreId]) {
        state.chores[choreId] = {
          ...state.chores[choreId],
          ...updates,
        }
      }
    },

    deleteChore: (state, action) => {
      const choreId = action.payload
      delete state.chores[choreId]
    },

    // Chore completion
    completeChore: (state, action) => {
      const { choreId, userId, completedAt } = action.payload
      const chore = state.chores[choreId]

      if (chore) {
        // Add to completed chores
        const completionKey = `${choreId}_${completedAt}`
        state.completedChores[completionKey] = {
          choreId,
          userId,
          completedAt,
          points: chore.points || 0,
          choreName: chore.name,
        }

        // Update user points
        if (!state.userPoints[userId]) {
          state.userPoints[userId] = 0
        }
        state.userPoints[userId] += chore.points || 0

        // Update user level based on points
        state.userLevels[userId] = calculateLevel(state.userPoints[userId])
      }
    },

    undoChoreCompletion: (state, action) => {
      const { choreId, userId, completedAt } = action.payload
      const completionKey = `${choreId}_${completedAt}`
      const completion = state.completedChores[completionKey]

      if (completion) {
        // Remove from completed chores
        delete state.completedChores[completionKey]

        // Subtract points
        if (state.userPoints[userId]) {
          state.userPoints[userId] -= completion.points
          // Recalculate level
          state.userLevels[userId] = calculateLevel(state.userPoints[userId])
        }
      }
    },

    // User management
    setActiveUsers: (state, action) => {
      state.activeUsers = action.payload
    },

    addUser: (state, action) => {
      const user = action.payload
      if (!state.activeUsers.find((u) => u.id === user.id)) {
        state.activeUsers.push(user)

        // Initialize user points and level if not exist
        if (!state.userPoints[user.id]) {
          state.userPoints[user.id] = 0
        }
        if (!state.userLevels[user.id]) {
          state.userLevels[user.id] = calculateLevel(0)
        }
      }
    },

    removeUser: (state, action) => {
      const userId = action.payload
      state.activeUsers = state.activeUsers.filter((u) => u.id !== userId)

      // Optionally keep points/levels for historical data
      // delete state.userPoints[userId];
      // delete state.userLevels[userId];
    },

    updateUser: (state, action) => {
      const { userId, updates } = action.payload
      const userIndex = state.activeUsers.findIndex((u) => u.id === userId)
      if (userIndex !== -1) {
        state.activeUsers[userIndex] = {
          ...state.activeUsers[userIndex],
          ...updates,
        }
      }
    },

    // Points and levels
    setUserPoints: (state, action) => {
      const { userId, points } = action.payload
      state.userPoints[userId] = points
      state.userLevels[userId] = calculateLevel(points)
    },

    addPointsToUser: (state, action) => {
      const { userId, points } = action.payload
      if (!state.userPoints[userId]) {
        state.userPoints[userId] = 0
      }
      state.userPoints[userId] += points
      state.userLevels[userId] = calculateLevel(state.userPoints[userId])
    },

    subtractPointsFromUser: (state, action) => {
      const { userId, points } = action.payload
      if (state.userPoints[userId]) {
        state.userPoints[userId] = Math.max(
          0,
          state.userPoints[userId] - points
        )
        state.userLevels[userId] = calculateLevel(state.userPoints[userId])
      }
    },

    resetUserPoints: (state, action) => {
      const userId = action.payload
      state.userPoints[userId] = 0
      state.userLevels[userId] = calculateLevel(0)
    },

    // Completed chores
    setCompletedChores: (state, action) => {
      state.completedChores = action.payload
    },

    clearCompletedChores: (state) => {
      state.completedChores = {}
    },

    // UI state
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },

    setError: (state, action) => {
      state.error = action.payload
      state.isLoading = false
    },

    clearError: (state) => {
      state.error = null
    },

    // Firestore hydration (handled by middleware)
    hydrateFromFirestore: (state, action) => {
      return {
        ...state,
        ...action.payload,
        _hydrated: true,
      }
    },
  },
})

// Helper function to calculate level based on points
const calculateLevel = (points) => {
  const level = Math.floor(points / 100) + 1
  const currentLevelMin = (level - 1) * 100
  const nextLevelMin = level * 100
  const progress = points - currentLevelMin
  const progressPercent = (progress / 100) * 100

  return {
    level,
    currentLevelMin,
    nextLevelMin,
    progress,
    progressPercent,
    pointsToNext: nextLevelMin - points,
  }
}

// Export actions
export const {
  setChores,
  addChore,
  updateChore,
  deleteChore,
  completeChore,
  undoChoreCompletion,
  setActiveUsers,
  addUser,
  removeUser,
  updateUser,
  setUserPoints,
  addPointsToUser,
  subtractPointsFromUser,
  resetUserPoints,
  setCompletedChores,
  clearCompletedChores,
  setLoading,
  setError,
  clearError,
  hydrateFromFirestore,
} = choresSlice.actions

// Selectors
export const selectChores = (state) => state.chores.chores
export const selectChoreById = (state, choreId) => state.chores.chores[choreId]
export const selectActiveUsers = (state) => state.chores.activeUsers
export const selectUserById = (state, userId) =>
  state.chores.activeUsers.find((u) => u.id === userId)
export const selectUserPoints = (state, userId) =>
  state.chores.userPoints[userId] || 0
export const selectUserLevel = (state, userId) =>
  state.chores.userLevels[userId] || calculateLevel(0)
export const selectAllUserPoints = (state) => state.chores.userPoints
export const selectAllUserLevels = (state) => state.chores.userLevels
export const selectCompletedChores = (state) => state.chores.completedChores
export const selectChoresLoading = (state) => state.chores.isLoading
export const selectChoresError = (state) => state.chores.error
export const selectChoresHydrated = (state) => state.chores._hydrated

// Composite selectors
export const selectUserRankings = (state) => {
  return state.chores.activeUsers
    .map((user) => ({
      ...user,
      points: state.chores.userPoints[user.id] || 0,
      level: state.chores.userLevels[user.id] || calculateLevel(0),
    }))
    .sort((a, b) => b.points - a.points)
}

export const selectRecentCompletions = (state, limit = 10) => {
  return Object.values(state.chores.completedChores)
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
    .slice(0, limit)
}

export const selectCompletionsByUser = (state, userId) => {
  return Object.values(state.chores.completedChores)
    .filter((completion) => completion.userId === userId)
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
}

// Enhanced reducer with Firestore hydration
export default withFirestoreHydration(choresSlice.reducer)
